<script lang="ts">
  import { onMount, tick } from 'svelte';
  import { npubEncode } from 'nostr-tools/nip19';
  import {
    credentialStatus, deleteBitcoinPlusPlusSession, deleteSession, getBadges, getBitcoinPlusPlusSession, getConfig, getHealth, getOrganizationAwards, getOrganizationBadges,
    getSession, linkBitcoinPlusPlusOrganization, retryOrganizationEvent, submitOrganizationEvent, type BadgeAward, type BadgeDefinition, type BitcoinPlusPlusOrganization, type BitcoinPlusPlusSession, type Organization, type PublicConfig
  } from './lib/api';
  import AwardStudio from './lib/AwardStudio.svelte';
  import BadgeEditor from './lib/BadgeEditor.svelte';
  import BitcoinWordmark from './lib/BitcoinWordmark.svelte';
  import ClaimPage from './lib/ClaimPage.svelte';
  import CredentialPage from './lib/CredentialPage.svelte';
  import CredentialExpiry from './lib/CredentialExpiry.svelte';
  import OrganizationProfile from './lib/OrganizationProfile.svelte';
  import OrganizationCatalog from './lib/OrganizationCatalog.svelte';
  import PeacockBadge from './lib/PeacockBadge.svelte';
  import SignerDialog from './lib/SignerDialog.svelte';
  import { restoreManagedBunker, type ManagedSignerSession, type SignerConnection } from './lib/signers';
  import { signBadgeRevocation } from './lib/badgeEvents';

  const claimEventID = typeof window === 'undefined' ? null : window.location.pathname.match(/^\/claim\/([0-9a-f]{64})\/?$/)?.[1] ?? null;
  const credentialMatch = typeof window === 'undefined' ? null : window.location.pathname.match(/^\/credentials\/([0-9a-f]{64})\/([0-9a-f]{64})\/?$/);
  const credentialEventID = credentialMatch?.[1] ?? null;
  const credentialRecipient = credentialMatch?.[2] ?? null;
  const catalogIssuer = typeof window === 'undefined' ? null : window.location.pathname.match(/^\/organizations\/([0-9a-f]{64})\/?$/)?.[1] ?? null;
	const catalogBitcoinPlusPlusOrganizationID = typeof window === 'undefined' ? null : window.location.pathname.match(/^\/btcpp\/organizations\/([^/]+)\/?$/)?.[1] ?? null;
  const requestedBitcoinPlusPlusOrganization = typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('btcpp_org') ?? '';
  let requestedGrantID = $state(typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('grant') ?? '');
	let requestedManagedBunker = $state(typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('managed_bunker') ?? '');
  let healthy = $state<boolean | null>(null);
  let config = $state<PublicConfig | null>(null);
  let badges = $state<BadgeDefinition[]>([]);
  let awards = $state<BadgeAward[]>([]);
  let error = $state('');
  let notice = $state('');
  let connectionNotice = $state('');
  let organization = $state<Organization | null>(null);
  let bitcoinPlusPlusSession = $state<BitcoinPlusPlusSession | null>(null);
  let selectedBitcoinPlusPlusOrganizationID = $state('');
  let signer = $state<SignerConnection | null>(null);
	let rememberedManagedSigner = $state<ManagedSignerSession | null>(null);
	let restoringManagedSigner = $state(false);
  let signerDialogOpen = $state(false);
	let pendingSignerAction = $state<'' | 'editor' | 'revoke'>('');
	let pendingRevocation = $state<BadgeAward | null>(null);
  let endingSession = $state(false);
  let organizationChooser = $state<HTMLSelectElement>();
  const canSwitchOrganizations = $derived((bitcoinPlusPlusSession?.organizations.filter((item) => item.can_manage).length ?? 0) > 1);
  let editorOpen = $state(false);
  let editingBadge = $state<BadgeDefinition | null>(null);
  let view = $state<'catalog' | 'issue' | 'history' | 'settings'>('catalog');
  let retrying = $state('');
  let revoking = $state('');
	let managedVaultReady = $derived(Boolean(rememberedManagedSigner && (!organization || rememberedManagedSigner.pubkey === organization.pubkey)));

  onMount(async () => {
    try {
      const [health, publicConfig, session, btcppSession] = await Promise.all([getHealth(), getConfig(), getSession(), getBitcoinPlusPlusSession()]);
      healthy = health.status === 'ok';
      config = publicConfig;
      organization = session;
      bitcoinPlusPlusSession = btcppSession;
      selectedBitcoinPlusPlusOrganizationID = btcppSession?.organizations.find((item) => item.can_manage && item.id === requestedBitcoinPlusPlusOrganization)?.id ?? btcppSession?.organizations.find((item) => item.can_manage)?.id ?? '';
			loadRememberedManagedSigner();
			if (requestedManagedBunker) {
				signerDialogOpen = true;
				connectionNotice = 'Bitcoin++ supplied your managed signer. Review and connect it to continue.';
			}
      const query = new URLSearchParams(window.location.search);
      if (query.get('btcpp_error')) error = query.get('btcpp_error')!;
      else if (query.get('btcpp') === 'connected' && !requestedManagedBunker) connectionNotice = 'Bitcoin++ connected. Choose an organization, then connect its signer.';
      if (requestedGrantID) {
        view = 'issue';
        const grantAvailable = btcppSession?.organizations.some((item) => item.can_manage && item.id === requestedBitcoinPlusPlusOrganization && item.badge_grants?.some((grant) => grant.id === requestedGrantID));
        if (!grantAvailable && publicConfig.bitcoinplusplus_enabled && query.get('btcpp') !== 'connected') {
          const returnTo = `${window.location.pathname}${window.location.search}`;
          window.location.replace(`/api/auth/btcpp/start?return_to=${encodeURIComponent(returnTo)}`);
          return;
        }
        if (!grantAvailable && query.get('btcpp') === 'connected') {
          error = 'This badge grant is no longer ready, or your Bitcoin++ account cannot manage its organization.';
        }
      }
			if (!claimEventID && !credentialEventID && !catalogIssuer && !catalogBitcoinPlusPlusOrganizationID) {
        if (session) {
					await loadWorkspace();
					if (!requestedManagedBunker) void restoreRememberedManagedSigner();
				}
        else badges = (await getBadges()).badges;
      }
    } catch (cause) {
      healthy = false;
      error = cause instanceof Error ? cause.message : 'Could not reach the badge service.';
    }
  });

  async function loadWorkspace() {
    const [badgeResult, awardResult] = await Promise.all([getOrganizationBadges(), getOrganizationAwards()]);
    badges = badgeResult.badges;
    awards = awardResult.awards;
  }

  async function connected(connection: SignerConnection, authenticatedOrganization: Organization) {
    const directoryOrganization = selectedBitcoinPlusPlusOrganization();
    const connectedOrganization = directoryOrganization
      ? (await linkBitcoinPlusPlusOrganization(directoryOrganization.id)).organization
      : authenticatedOrganization;
		organization = connectedOrganization;
    signer = connection;
    connectionNotice = '';
		if (directoryOrganization && connection.managedSession) rememberManagedSigner(directoryOrganization.id, connection.managedSession);
    signerDialogOpen = false;
		if (requestedManagedBunker) {
			const cleaned = new URL(window.location.href);
			cleaned.searchParams.delete('managed_bunker');
			history.replaceState(null, '', `${cleaned.pathname}${cleaned.search}${cleaned.hash}`);
			requestedManagedBunker = '';
    }
    await loadWorkspace();
		const completedAction = pendingSignerAction;
		const awardToRevoke = pendingRevocation;
		pendingSignerAction = '';
		pendingRevocation = null;
		if (completedAction === 'editor') editorOpen = true;
		else if (completedAction === 'revoke' && awardToRevoke) void revoke(awardToRevoke);
  }

	function managedSignerStorageKey(organizationID: string): string {
		return `btcpp.badges.managed-signer.${organizationID}`;
	}

	function loadRememberedManagedSigner() {
		rememberedManagedSigner = null;
		if (!selectedBitcoinPlusPlusOrganizationID) return;
		try {
			const candidate = JSON.parse(localStorage.getItem(managedSignerStorageKey(selectedBitcoinPlusPlusOrganizationID)) ?? 'null') as ManagedSignerSession | null;
			if (!candidate || !candidate.bunkerURL.startsWith('bunker://') || (candidate.clientSecret !== undefined && !/^[0-9a-f]{64}$/.test(candidate.clientSecret)) || !/^[0-9a-f]{64}$/.test(candidate.pubkey)) return;
			rememberedManagedSigner = candidate;
		} catch {
			localStorage.removeItem(managedSignerStorageKey(selectedBitcoinPlusPlusOrganizationID));
		}
	}

	function rememberManagedSigner(organizationID: string, session: ManagedSignerSession) {
		localStorage.setItem(managedSignerStorageKey(organizationID), JSON.stringify(session));
		if (organizationID === selectedBitcoinPlusPlusOrganizationID) rememberedManagedSigner = session;
	}

	async function restoreRememberedManagedSigner() {
		const session = rememberedManagedSigner;
		const organizationID = selectedBitcoinPlusPlusOrganizationID;
		if (!session?.clientSecret || !organization || session.pubkey !== organization.pubkey || signer || restoringManagedSigner) return;
		restoringManagedSigner = true;
		const controller = new AbortController();
		const timeout = window.setTimeout(() => controller.abort(new Error('Managed signer restoration timed out.')), 5000);
		try {
			const connection = await restoreManagedBunker(session, controller.signal);
			if (organizationID !== selectedBitcoinPlusPlusOrganizationID || organization?.pubkey !== connection.pubkey) {
				await connection.close();
				return;
			}
			signer = connection;
			connectionNotice = '';
		} catch {
			// The organization-vault association is still useful even when its relay
			// connection is temporarily unavailable or has been revoked.
		} finally {
			window.clearTimeout(timeout);
			restoringManagedSigner = false;
		}
	}

	function organizationSelectionChanged() {
		const destination = new URL(window.location.href);
		destination.searchParams.set('btcpp_org', selectedBitcoinPlusPlusOrganizationID);
		history.replaceState(null, '', `${destination.pathname}${destination.search}${destination.hash}`);
		loadRememberedManagedSigner();
		if (organization) void restoreRememberedManagedSigner();
	}

	function requestSigner(action: '' | 'editor' | 'revoke' = '') {
		pendingSignerAction = action;
		signerDialogOpen = true;
	}

	function closeSignerDialog() {
		signerDialogOpen = false;
		pendingSignerAction = '';
		pendingRevocation = null;
	}

  function selectedBitcoinPlusPlusOrganization(): BitcoinPlusPlusOrganization | null {
    return bitcoinPlusPlusSession?.organizations.find((item) => item.id === selectedBitcoinPlusPlusOrganizationID && item.can_manage) ?? null;
  }

  function clearOrganizationWorkspace() {
    organization = null;
    awards = [];
    badges = [];
    editingBadge = null;
    requestedGrantID = '';
    view = 'catalog';
    connectionNotice = '';
    notice = '';
    signerDialogOpen = false;
    editorOpen = false;
    pendingSignerAction = '';
    pendingRevocation = null;
    requestedManagedBunker = '';
    const cleaned = new URL(window.location.href);
    for (const key of ['btcpp', 'btcpp_org', 'managed_bunker', 'btcpp_error', 'grant']) cleaned.searchParams.delete(key);
    history.replaceState(null, '', `${cleaned.pathname}${cleaned.search}${cleaned.hash}`);
  }

  async function switchOrganizations() {
    if (endingSession || !canSwitchOrganizations) return;
    endingSession = true;
    error = '';
    try {
      await deleteSession();
      const previousSigner = signer;
      signer = null;
      clearOrganizationWorkspace();
      // Switching preserves the revocable credentials for returning later.
      try { await previousSigner?.close(); } catch { /* the workspace is already disconnected */ }
      organizationSelectionChanged();
      await tick();
      organizationChooser?.focus();
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Could not switch organizations. Please try again.';
    } finally {
      endingSession = false;
    }
  }

  async function signOut() {
    if (endingSession) return;
    endingSession = true;
    error = '';
    const organizationID = selectedBitcoinPlusPlusOrganizationID;
    try {
      await deleteSession();
      try {
        if (signer?.logout) await signer.logout();
        else await signer?.close();
      } catch {
        // Clear local credentials even when the relay cannot accept logout.
      } finally {
        if (organizationID) localStorage.removeItem(managedSignerStorageKey(organizationID));
        signer = null;
        rememberedManagedSigner = null;
      }
      clearOrganizationWorkspace();
      await deleteBitcoinPlusPlusSession();
      bitcoinPlusPlusSession = null;
      selectedBitcoinPlusPlusOrganizationID = '';
      badges = (await getBadges()).badges;
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Could not finish signing out. Please try again.';
    } finally {
      endingSession = false;
    }
  }

  function openEditor(badge: BadgeDefinition | null = null) {
		editingBadge = badge;
    if (!signer) {
			requestSigner('editor');
      return;
    }
    editorOpen = true;
  }

  async function badgeSaved() {
    notice = editingBadge ? 'Badge definition updated.' : 'Your new badge is ready to award.';
    badges = (await getOrganizationBadges()).badges;
  }

  async function awardsIssued() {
    awards = (await getOrganizationAwards()).awards;
  }

  async function retry(eventID: string) {
    retrying = eventID;
    notice = '';
    try {
      const result = await retryOrganizationEvent(eventID);
      notice = result.published ? 'At least one relay accepted the event.' : 'No relay accepted the event yet.';
      await loadWorkspace();
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Could not retry publication.';
    } finally {
      retrying = '';
    }
  }

  async function revoke(award: BadgeAward) {
		if (!signer) {
			pendingRevocation = award;
			requestSigner('revoke');
			return;
		}
    if (award.revocation || !confirm('Revoke this badge award? This publishes a permanent Nostr deletion request and cannot be undone.')) return;
    revoking = award.event_id;
    error = '';
    try {
      const event = await signBadgeRevocation(signer, award.event_id);
      const result = await submitOrganizationEvent(event);
      notice = result.published ? 'Award revoked and the deletion request reached a relay.' : 'Award marked revoked; no relay accepted the deletion request yet.';
      await loadWorkspace();
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Could not revoke award.';
    } finally {
      revoking = '';
    }
  }

  async function copyClaim(eventID: string) {
    await navigator.clipboard.writeText(`${window.location.origin}/claim/${eventID}`);
    notice = 'Claim link copied.';
  }

  async function copyOrganizationKey() {
    if (!organization) return;
    await navigator.clipboard.writeText(npubEncode(organization.pubkey));
    notice = 'Organization npub copied.';
  }

  function profileSaved(updated: Organization) {
    organization = updated;
    notice = 'Credential identity saved.';
  }

  function hideBrokenArtwork(event: Event) {
    (event.currentTarget as HTMLImageElement).hidden = true;
  }

  function awardArtwork(award: BadgeAward): string | null {
    return award.badge?.thumbnails.find((thumb) => thumb.width === 64)?.url ?? award.badge?.image_url ?? null;
  }

  function shortIdentity(pubkey: string): string {
    const npub = npubEncode(pubkey);
    return `${npub.slice(0, 12)}…${npub.slice(-7)}`;
  }

  function compactIdentity(pubkey: string): string {
    const npub = npubEncode(pubkey);
    return `${npub.slice(0, 8)}…${npub.slice(-4)}`;
  }

  function acceptedCount(publications: BadgeAward['publications']): number {
    return new Set(publications.filter((item) => item.accepted).map((item) => item.relay_url)).size;
  }
</script>

<svelte:head>
	<title>{credentialEventID ? 'Verified badge credential' : claimEventID ? 'Accept your Nostr badge' : catalogIssuer || catalogBitcoinPlusPlusOrganizationID ? 'Organization badge catalog' : 'Badge Studio by Bitcoin++'}</title>
  <meta name="description" content="Create and issue portable Nostr badges for the people who make communities matter." />
</svelte:head>

{#if credentialEventID && credentialRecipient}
  {#if config}<CredentialPage eventID={credentialEventID} recipient={credentialRecipient} {config} />{:else}<main class="boot"><PeacockBadge /><p>Opening your credential…</p></main>{/if}
{:else if claimEventID}
  {#if config}<ClaimPage eventID={claimEventID} {config} />{:else}<main class="boot"><PeacockBadge /><p>Opening your award…</p></main>{/if}
{:else if catalogIssuer}
  <OrganizationCatalog issuer={catalogIssuer} />
{:else if catalogBitcoinPlusPlusOrganizationID}
	<OrganizationCatalog bitcoinPlusPlusOrganizationID={catalogBitcoinPlusPlusOrganizationID} />
{:else}
  <header class:authenticated={organization} class="site-header">
    <a class="brand" href="/" aria-label="Badge Studio by Bitcoin++ home">
      <BitcoinWordmark />
      <span class="brand-name"><span class="brand-long">Badge Studio</span><span class="brand-short">Studio</span><small>Nostr badge tools</small></span>
    </a>
    {#if organization}
      <div class="identity">
        <button class="identity-copy" type="button" title="Copy signed-in organization npub" aria-label="Copy signed-in organization npub" onclick={copyOrganizationKey}><small>Signed in as</small><span class="identity-desktop">{shortIdentity(organization.pubkey)}</span><span class="identity-mobile">{compactIdentity(organization.pubkey)}</span></button>
        <div class="identity-actions">
          <button class="quiet-button" type="button" onclick={signOut} disabled={endingSession}>Sign out</button>
          {#if canSwitchOrganizations}<button class="switch-organization" type="button" onclick={switchOrganizations} disabled={endingSession}>Switch organization</button>{/if}
        </div>
      </div>
    {:else}
      <button class="outline-button" type="button" onclick={() => signerDialogOpen = true} disabled={!config}>
        <span class="connect-label"><span>Connect signer</span><small>Connect</small></span><i aria-hidden="true">↗</i>
      </button>
    {/if}
  </header>

  <main class:dashboard={organization}>
    {#if organization}
      <section class="dashboard-head">
        <div><p class="eyebrow">organization workspace</p><h1>Your badge desk.</h1></div>
        <div class="dashboard-status" aria-label="Signer and service status"><span><i class:online={Boolean(signer)} class:standby={!signer && managedVaultReady}></i>{signer ? 'Signer connected' : restoringManagedSigner ? 'Restoring key vault' : managedVaultReady ? 'Key vault ready' : 'Signer required'}</span><small>{healthy ? 'service online' : 'checking service'} · {config?.relays.length ?? 0} publication relays</small></div>
      </section>

      <nav class="studio-nav" aria-label="Studio sections">
        <button class:active={view === 'catalog'} type="button" onclick={() => view = 'catalog'}><span>01</span> Catalog <b>{badges.length}</b></button>
        <button class:active={view === 'issue'} type="button" onclick={() => view = 'issue'}><span>02</span> Issue</button>
        <button class:active={view === 'history'} type="button" onclick={() => view = 'history'}><span>03</span> History <b>{awards.length}</b></button>
        <button class:active={view === 'settings'} type="button" onclick={() => view = 'settings'}><span>04</span> Identity</button>
      </nav>

      {#if !signer && !managedVaultReady && !restoringManagedSigner}
        <aside class="reconnect">
          <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M15 22v-5a9 9 0 0118 0v5M11 22h26v20H11z"/><circle cx="24" cy="31" r="2"/><path d="M24 33v4"/></svg>
          <div><strong>Signer required to publish.</strong><p>You’re still signed in and can browse this organization. Connect its signer when you want to create or issue a badge.</p></div>
          <button type="button" onclick={() => signerDialogOpen = true}>Connect organization signer <span>→</span></button>
        </aside>
      {/if}

      {#if notice || connectionNotice}<div class="notice" role="status"><span>✦</span>{notice || connectionNotice}<button type="button" aria-label="Dismiss message" onclick={() => { notice = ''; connectionNotice = ''; }}>×</button></div>{/if}
      {#if error}<div class="app-error" role="alert">{error}<button type="button" aria-label="Dismiss error" onclick={() => error = ''}>×</button></div>{/if}

      <section class="workspace">
        {#if view === 'catalog'}
          <div class="section-heading">
            <div><p class="eyebrow">01 · catalog</p><h2>Your badges.</h2></div>
            <button class="primary" type="button" onclick={() => openEditor()}>{signer || managedVaultReady ? 'New badge' : 'Connect signer to create'} <span>＋</span></button>
          </div>
          {#if badges.length === 0}
            <button class="empty-state" type="button" onclick={() => openEditor()}>
              <div class="empty-seal" aria-hidden="true"><span>＋</span></div>
              <div><small>CATALOG / EMPTY</small><h3>Create your first badge</h3><p>Design an emblem, describe what it means, and publish it with your organization key.</p></div>
              <span class="empty-index">01</span>
            </button>
          {:else}
            <div class="badge-grid">
              {#each badges as badge, index}
                <article style={`--stagger:${index * 55}ms`}>
                  <button class="badge-card" type="button" onclick={() => openEditor(badge)}>
                    <div class="badge-image"><span class="badge-orbit"></span><span class="badge-initial" aria-hidden="true">{badge.name.slice(0, 1)}</span><img src={badge.image_url} alt={`Artwork for ${badge.name}`} onerror={hideBrokenArtwork} /></div>
                    <small>BADGE / {String(index + 1).padStart(2, '0')}</small>
                    <h3>{badge.name}</h3><p>{badge.description}</p>
                    <i>Edit definition →</i>
                  </button>
                </article>
              {/each}
            </div>
          {/if}
        {:else if view === 'issue'}
          <div class="section-heading"><div><p class="eyebrow">02 · award desk</p><h2>Issue a badge.</h2></div></div>
          {#if signer}<AwardStudio canBrowseEcosystem={bitcoinPlusPlusSession?.can_browse_ecosystem ?? false} {badges} {signer} relays={config?.relays ?? []} onissued={awardsIssued} directoryOrganization={selectedBitcoinPlusPlusOrganization()} bitcoinPlusPlusURL={config?.bitcoinplusplus_url ?? ''} initialGrantID={requestedGrantID} />{:else if managedVaultReady}<div class="locked-view"><p>Your organization key vault is ready. Unlock it when you’re ready to prepare an award.</p><button class="primary" type="button" onclick={() => requestSigner()}>Unlock key vault <span>→</span></button></div>{:else}<div class="locked-view"><p>Connect your organization signer above to prepare an award.</p></div>{/if}
        {:else if view === 'history'}
          <div class="section-heading"><div><p class="eyebrow">03 · public record</p><h2>Award history.</h2></div></div>
          {#if awards.length === 0}
            <div class="history-empty"><span>03</span><p>No awards have been issued from this organization yet.</p></div>
          {:else}
            <div class="history-list">
              {#each awards as award}
                {@const artwork = awardArtwork(award)}
                <article class:revoked={Boolean(award.revocation)}>
                  <div class="history-art" aria-hidden="true"><span>{(award.badge?.name ?? 'Badge').slice(0, 1)}</span>{#if artwork}<img src={artwork} alt="" onerror={hideBrokenArtwork} />{/if}</div>
                  <div class="history-title"><small>{award.revocation ? 'revoked' : award.recipients.length === 1 && credentialStatus(award, award.recipients[0]) === 'expired' ? 'expired' : new Date(award.created_at).toLocaleDateString()}</small><strong>{award.badge?.name ?? award.badge_address}</strong><span>{award.recipients.length} {award.recipients.length === 1 ? 'recipient' : 'recipients'}</span></div>
                  <div class="relay-count" class:delivered={acceptedCount(award.publications) > 0}><i></i><span>{acceptedCount(award.publications)} {acceptedCount(award.publications) === 1 ? 'relay' : 'relays'}</span></div>
                  <div class="history-actions"><button type="button" onclick={() => copyClaim(award.event_id)}>Copy claim link</button>{#if award.revocation}<button type="button" onclick={() => retry(award.revocation!.event_id)} disabled={retrying === award.revocation.event_id}>{retrying === award.revocation.event_id ? 'Retrying…' : 'Retry revocation'}</button>{:else}<button type="button" onclick={() => retry(award.event_id)} disabled={retrying === award.event_id}>{retrying === award.event_id ? 'Retrying…' : 'Retry relays'}</button><button type="button" onclick={() => revoke(award)} disabled={revoking === award.event_id}>{revoking === award.event_id ? 'Revoking…' : 'Revoke'}</button>{/if}</div>
                <CredentialExpiry {award} onupdated={async () => { awards = (await getOrganizationAwards()).awards; }} />
                </article>
              {/each}
            </div>
          {/if}
        {:else}
          <div class="section-heading"><div><p class="eyebrow">04 · organization profile</p><h2>Credential identity.</h2></div></div>
          <OrganizationProfile {organization} onsaved={profileSaved} />
        {/if}
      </section>
    {:else}
      <section class="hero">
        <div class="hero-copy">
          <p class="eyebrow"><span>portable recognition</span><span>nip-58</span></p>
          <h1>Make the work <em>visible.</em></h1>
          <p class="lede">Create and issue meaningful badges for the people who teach, build, host, and hold communities together.</p>
          {#if bitcoinPlusPlusSession}
            <div class="btcpp-welcome">
              <p>signed in with bitcoin++ as <strong>{bitcoinPlusPlusSession.name}</strong></p>
              {#if bitcoinPlusPlusSession.organizations.some((item) => item.can_manage)}
                <label><span>Organization</span><select bind:this={organizationChooser} bind:value={selectedBitcoinPlusPlusOrganizationID} onchange={organizationSelectionChanged}>{#each bitcoinPlusPlusSession.organizations.filter((item) => item.can_manage) as item}<option value={item.id}>{item.name} · {item.role}</option>{/each}</select></label>
                <button class="hero-action" type="button" onclick={() => requestSigner()}>{managedVaultReady ? 'Open organization key vault' : 'Connect organization signer'} <span>→</span></button>
              {:else}
                <p>Your account has no managed organizations yet. You can still use Badge Studio with any Nostr signer.</p>
                <button class="hero-action" type="button" onclick={() => signerDialogOpen = true}>Continue with a Nostr signer <span>→</span></button>
              {/if}
              <button class="btcpp-signout" type="button" onclick={signOut} disabled={endingSession}>disconnect bitcoin++</button>
            </div>
          {:else if config?.bitcoinplusplus_enabled}
            <a class="hero-action" href="/api/auth/btcpp/start">Sign in with bitcoin++ <span>→</span></a>
            <button class="nostr-alternative" type="button" onclick={() => signerDialogOpen = true}>or continue with any Nostr signer</button>
          {:else}
            <button class="hero-action" type="button" onclick={() => signerDialogOpen = true} disabled={!config}>Open your studio <span>→</span></button>
          {/if}
          <div class="promise-list"><span>Signed by your organization</span><span>Held by their npub</span><span>Recognized across Nostr</span></div>
        </div>
        <div class="credential-board" aria-label="A peacock-inspired portable badge">
          <span class="edition">FIELD NOTE / 001</span><PeacockBadge />
          <div class="specimen-caption"><div><small>ISSUED FOR</small><strong>Community work</strong></div><span>№ 58</span></div>
          <span class="registration">SIGNED • PORTABLE • OPEN</span>
        </div>
      </section>
    {/if}
  </main>

  <footer><span class="footer-brand"><span>badge studio by</span><BitcoinWordmark /></span><span class="footer-note">open recognition for open communities.</span><span>{config?.relays.length ?? 0} publication relays</span></footer>

	{#if signerDialogOpen && config}<SignerDialog {config} managedTenantID={selectedBitcoinPlusPlusOrganization()?.id ?? ''} initialBunker={requestedManagedBunker || (managedVaultReady ? rememberedManagedSigner?.bunkerURL ?? '' : '')} onconnected={connected} onclose={closeSignerDialog} />{/if}
  {#if editorOpen && signer}<BadgeEditor {signer} bitcoinPlusPlusURL={config?.bitcoinplusplus_url} badge={editingBadge} onclose={() => editorOpen = false} onsaved={badgeSaved} />{/if}
{/if}
